import { GeneralCustomsDeclaration, LineItem } from '~api/som';

import { ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Add16, Bin16 } from '@ingrid/iconography/react';
import {
  Button,
  ButtonLink,
  DateValuePicker,
  FormControl,
  FormItem,
  IconButton,
  ListLayout,
  Modal,
  ModalLayout,
  TextInput,
  Tooltip,
} from '@ingrid/luterkort-react';

import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { CountrySelect } from '~src/components/CountrySelect/CountrySelect';
import { CurrencySelect } from '~src/components/CurrencySelect/CurrencySelect';
import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { NumberInput } from '~src/components/NumberInput/NumberInput';
import { SimpleSelect } from '~src/components/SimpleSelect/SimpleSelect';
import { LocalizationKey, useTranslation } from '~src/i18n/use-translation';
import { fromAPIAmount, toAmountAPIUpsert } from '~src/utils/amount';
import { removeByIndex } from '~src/utils/array';
import { toLocalCalendarDate } from '~src/utils/internationalized-date';

import { contentsTypes, incoTerms, useShipmentFormContext } from '../types';

export interface Props {
  onSubmit: (values: GeneralCustomsDeclaration) => void;
  defaultValues?: GeneralCustomsDeclaration;
  goods?: LineItem[];
  trigger: ReactNode;
}

export function CustomsDeclarationsAdd({ defaultValues, trigger, onSubmit: onSubmitProp, goods = [] }: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  const { register, handleSubmit, reset, setValue, watch, getValues, getFieldState, control, formState } =
    useForm<GeneralCustomsDeclaration>({
      defaultValues: {
        ...defaultValues,
        contents_explanation: defaultValues?.contents_explanation ?? '',
        seller_tax_id_number: defaultValues?.seller_tax_id_number ?? '',
        buyer_tax_id_number: defaultValues?.buyer_tax_id_number ?? '',
        total_gross_weight: defaultValues?.total_gross_weight ?? 0,
        // This is fine to reset to `undefined` since it's a custom controlled component (select)
        contents_type: defaultValues?.contents_type ?? undefined,
        // This is fine to reset to `undefined` since it's a custom controlled component (select)
        incoterms: defaultValues?.incoterms ?? undefined,
        invoice_number: defaultValues?.invoice_number ?? '',
        invoice_date: defaultValues?.invoice_date ?? '',
        currency: defaultValues?.currency ?? '',
        sold_to: {
          address_lines: defaultValues?.sold_to?.address_lines ?? [''],
          postal_code: defaultValues?.sold_to?.postal_code ?? '',
          country: defaultValues?.sold_to?.country ?? '',
          region: defaultValues?.sold_to?.region ?? '',
          name: defaultValues?.sold_to?.name ?? '',
          city: defaultValues?.sold_to?.city ?? '',
        },
        items: defaultValues?.items?.map(item => ({
          unit_value: fromAPIAmount({ value: item.unit_value, currency: defaultValues.currency }) ?? 0,
          country_of_origin: item.country_of_origin ?? '',
          unit_gross_weight: item.unit_gross_weight ?? 0,
          hs_tariff_number: item.hs_tariff_number ?? '',
          unit_net_weight: item.unit_net_weight ?? 0,
          category: item.category ?? '',
          quantity: item.quantity ?? 1,
          brand: item.brand ?? '',
          name: item.name ?? '',
          sku: item.sku ?? '',
        })),
      },
    });

  const { t, i18n } = useTranslation();
  const shipment = useShipmentFormContext();

  const soldToAddressLines = watch('sold_to')?.address_lines ?? [];
  const disableAppendSoldToAddressLine =
    soldToAddressLines.length >= 4 || soldToAddressLines[soldToAddressLines.length - 1] === '';

  const disableLoadConsigmentData = !watch('currency') || !goods.length;
  const disableLoadConsigneeAddress = !shipment.watch('customer_info.contact');
  const items = watch('items') ?? [];

  function onSubmit(values: GeneralCustomsDeclaration) {
    onSubmitProp({
      ...values,
      items: items.map(item => ({
        ...item,
        unit_value: toAmountAPIUpsert({ value: item.unit_value, currency: values.currency }),
      })),
    });

    setModalOpen(false);
  }

  function onOpenChange(isOpen: boolean) {
    setModalOpen(isOpen);

    if (!isOpen) {
      reset();
    }
  }

  function loadConsigneeAddress() {
    const contact = shipment.getValues('customer_info.contact.address');

    setValue('sold_to', {
      address_lines: contact?.address_lines,
      postal_code: contact?.postal_code,
      country: contact?.country,
      region: contact?.region,
      city: contact?.city,
      name: contact?.name,
    });
  }

  function loadConsigmentData() {
    const lineItems = goods.map(item => ({
      unit_value: fromAPIAmount({ value: item.price, currency: getValues('currency') }),
      hs_tariff_number: item.hs_tariff_number,
      unit_gross_weight: item.weight,
      quantity: item.quantity,
      name: item.name,
      sku: item.sku,
    }));

    setValue('items', lineItems);
  }

  function handleAddItem() {
    setValue(`items.${items.length}`, {
      country_of_origin: '',
      unit_gross_weight: 0,
      hs_tariff_number: '',
      unit_net_weight: 0,
      unit_value: 0,
      category: '',
      quantity: 1,
      brand: '',
      name: '',
      sku: '',
    });
  }

  return (
    <FormControl.Root>
      <Modal.Root open={modalOpen} onOpenChange={onOpenChange}>
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content>
          <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <Modal.Header title={t('common.customsDeclarations')} />

          <ModalLayout.Body>
            <ModalLayout.Section>
              <ModalLayout.Title>{t('common.generalInformation')}</ModalLayout.Title>

              <ModalLayout.Content css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '16px' }}>
                <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                  <FormItem.Label>{t('common.incoterms')}</FormItem.Label>
                  <FormItem.Control>
                    <Controller
                      control={control}
                      name="incoterms"
                      rules={{ required: true }}
                      render={({ field: { value, onChange, ref } }) => (
                        <SimpleSelect
                          aria-required="true"
                          value={value ? [value] : undefined}
                          onValueChange={v => onChange(v[0] as GeneralCustomsDeclaration['incoterms'])}
                          invalid={!!formState.errors.incoterms}
                          options={incoTerms.map(v => [
                            v,
                            t(`shipments.customsDeclarations.incoterms.${v}` as LocalizationKey),
                          ])}
                          placeholder={t('common.selectIncoterms')}
                          ref={ref}
                        />
                      )}
                    />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '7 / span 3' }}>
                  <FormItem.Label>{t('common.totalGrossWeight')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.total_gross_weight}>
                    <NumberInput
                      {...register('total_gross_weight', {
                        valueAsNumber: true,
                        min: 0,
                      })}
                      data-invalid={!!formState.errors.total_gross_weight}
                      decimals={false}
                      suffix="g"
                    />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '10 / -1' }}>
                  <FormItem.Label>{t('common.purchaseCurrency')}</FormItem.Label>
                  <FormItem.Control>
                    <Controller
                      render={({ field, fieldState: { invalid } }) => <CurrencySelect invalid={invalid} {...field} />}
                      // Should only be required if there's an item with a amount specified
                      rules={{
                        required:
                          watch('items')?.some(i => i.unit_value !== undefined) ??
                          goods.some(i => i.price !== undefined),
                      }}
                      control={control}
                      name="currency"
                    />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                  <FormItem.Label>{t('shipments.customsDeclarations.contentsType')}</FormItem.Label>
                  <FormItem.Control>
                    <Controller
                      control={control}
                      name="contents_type"
                      rules={{ required: true }}
                      render={({ field: { value, onChange, ref } }) => (
                        <SimpleSelect
                          aria-required="true"
                          value={value ? [value] : undefined}
                          onValueChange={v => onChange(v[0] as GeneralCustomsDeclaration['contents_type'])}
                          invalid={!!formState.errors.contents_type}
                          options={contentsTypes.map(v => [
                            v,
                            t(`shipments.customsDeclarations.contentsType.${v}` as LocalizationKey),
                          ])}
                          placeholder={t('shipments.customsDeclarations.selectContentsType')}
                          ref={ref}
                        />
                      )}
                    />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                  <FormItem.Label>{t('shipments.customsDeclarations.contentsExplanation')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.contents_explanation}>
                    <TextInput {...register('contents_explanation')} />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                  <FormItem.Label>{t('common.invoiceNumber')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.invoice_number}>
                    <TextInput {...register('invoice_number')} />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                  <FormItem.Label>{t('common.invoiceDate')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.invoice_date}>
                    <Controller
                      control={control}
                      name="invoice_date"
                      render={({ field: { value, onChange } }) => (
                        <DateValuePicker
                          {...register('invoice_date')}
                          value={value ? toLocalCalendarDate(value) : undefined}
                          onChange={value => onChange(value!.toString())}
                          isInvalid={!!formState.errors.invoice_date}
                          placeholder={t('common.selectDate')}
                          locale={i18n.resolvedLanguage}
                        />
                      )}
                    />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                  <FormItem.Label>{t('common.sellerTaxIdNumber')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.seller_tax_id_number}>
                    <TextInput {...register('seller_tax_id_number')} />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                  <FormItem.Label>{t('common.buyerTaxIdNumber')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.buyer_tax_id_number}>
                    <TextInput {...register('buyer_tax_id_number')} />
                  </FormItem.Control>
                </FormItem.Root>
              </ModalLayout.Content>
            </ModalLayout.Section>

            <ModalLayout.Section>
              <ModalLayout.Row>
                <ModalLayout.Title>{t('common.payer')}</ModalLayout.Title>
                <ModalLayout.Actions>
                  <ConditionalWrapper
                    when={disableLoadConsigneeAddress}
                    wrapper={c => (
                      <Tooltip content={t('shipments.customsDeclarations.useConsigneeAddressDisabled')}>
                        <div>{c}</div>
                      </Tooltip>
                    )}
                  >
                    <ButtonLink onClick={loadConsigneeAddress} disabled={disableLoadConsigneeAddress}>
                      {t('shipments.address.useConsigneeAddress')}
                    </ButtonLink>
                  </ConditionalWrapper>
                </ModalLayout.Actions>
              </ModalLayout.Row>

              <ModalLayout.Content css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '16px' }}>
                <FormItem.Root css={{ gridColumn: '1 / -1' }}>
                  <FormItem.Label>{t('common.name')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.sold_to?.name}>
                    <TextInput {...register('sold_to.name')} />
                  </FormItem.Control>
                </FormItem.Root>

                <ListLayout.Column style={{ display: 'flex', gap: '16px', width: '100%', gridColumn: '1 / -1' }}>
                  {soldToAddressLines.map((_, index) => {
                    const addressLineFieldState = getFieldState(`sold_to.address_lines.${index}`);

                    return (
                      <FormItem.Root style={{ width: '100%' }} key={`field-arr-${index}`}>
                        <FormItem.Label>{t('common.addressLineIndex', { index: index + 1 })}</FormItem.Label>
                        <FormItem.Control invalid={addressLineFieldState.invalid}>
                          <ListLayout.Stack>
                            <ListLayout.Row style={{ gap: '16px' }}>
                              <TextInput
                                data-invalid={addressLineFieldState.invalid}
                                {...register(`sold_to.address_lines.${index}`)}
                              />

                              <IconButton
                                aria-label={t('common.removeItem', { item: 1 })}
                                onClick={() =>
                                  setValue('sold_to.address_lines', removeByIndex(soldToAddressLines, index))
                                }
                                style={{ flexShrink: '0' }}
                                disabled={index === 0}
                                variant="secondary"
                              >
                                <Bin16 />
                              </IconButton>
                            </ListLayout.Row>
                          </ListLayout.Stack>
                        </FormItem.Control>

                        {addressLineFieldState.error?.message && (
                          <FormItem.ErrorText>{addressLineFieldState.error.message}</FormItem.ErrorText>
                        )}
                      </FormItem.Root>
                    );
                  })}

                  <div>
                    <ButtonLink
                      onClick={() => setValue('sold_to.address_lines', [...soldToAddressLines, ''])}
                      disabled={disableAppendSoldToAddressLine}
                    >
                      <Add16 />
                      {t('common.addAddressLine')}
                    </ButtonLink>
                  </div>
                </ListLayout.Column>

                <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                  <FormItem.Label>{t('common.city')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.sold_to?.city}>
                    <TextInput {...register('sold_to.city')} />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                  <FormItem.Label>{t('common.country')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.sold_to?.country}>
                    <Controller
                      control={control}
                      name="sold_to.country"
                      render={({ field }) => (
                        <FormItem.Control>
                          <CountrySelect
                            {...field}
                            invalid={!!formState.errors.sold_to?.country?.message}
                            onChange={value => {
                              field.onChange(value);
                              field.onBlur();
                            }}
                          />
                        </FormItem.Control>
                      )}
                    />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                  <FormItem.Label>{t('common.region')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.sold_to?.region}>
                    <TextInput {...register('sold_to.region')} />
                  </FormItem.Control>
                </FormItem.Root>

                <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                  <FormItem.Label>{t('common.postalCode')}</FormItem.Label>
                  <FormItem.Control invalid={!!formState.errors.sold_to?.postal_code}>
                    <TextInput {...register('sold_to.postal_code')} />
                  </FormItem.Control>
                </FormItem.Root>
              </ModalLayout.Content>
            </ModalLayout.Section>

            <ModalLayout.Section>
              <ModalLayout.Row>
                <ModalLayout.Title>{t('common.itemsDetails')}</ModalLayout.Title>
                <ModalLayout.Actions>
                  <ConditionalWrapper
                    when={disableLoadConsigmentData}
                    wrapper={c => (
                      <Tooltip content={t('shipments.customsDeclarations.useConsignmentDataDisabled')}>
                        <div>{c}</div>
                      </Tooltip>
                    )}
                  >
                    <ButtonLink onClick={loadConsigmentData} disabled={disableLoadConsigmentData}>
                      {t('shipments.customsDeclarations.useConsignmentData')}
                    </ButtonLink>
                  </ConditionalWrapper>
                </ModalLayout.Actions>
              </ModalLayout.Row>

              {items.map((_, i) => (
                <ModalLayout.InnerContent key={`item-${i}`}>
                  <ModalLayout.Actions>
                    <ButtonLink onClick={() => setValue(`items`, [...items.slice(0, i), ...items.slice(i + 1)])}>
                      {t('common.clear')}
                    </ButtonLink>
                  </ModalLayout.Actions>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '16px' }}>
                    <FormItem.Root css={{ gridColumn: '1 / span 1' }}>
                      <FormItem.Label>{t('common.quantity')}</FormItem.Label>
                      <FormItem.Control invalid={!!formState.errors.items?.[i]?.quantity}>
                        <NumberInput
                          {...register(`items.${i}.quantity`, { valueAsNumber: true, required: true, min: 1 })}
                          data-invalid={!!formState.errors.items?.[i]?.quantity}
                          decimals={false}
                        />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '2 / -1' }}>
                      <FormItem.Label>{t('common.name')}</FormItem.Label>
                      <FormItem.Control>
                        <TextInput {...register(`items.${i}.name`)} />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '1 / span 7' }}>
                      <FormItem.Label>SKU</FormItem.Label>
                      <FormItem.Control invalid={!!formState.errors.items?.[i]?.sku}>
                        <TextInput aria-required="true" {...register(`items.${i}.sku`, { required: true })} />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '8 / -1' }}>
                      <FormItem.Label>{t('common.value')}</FormItem.Label>
                      <FormItem.Control invalid={!!formState.errors.items?.[i]?.unit_value}>
                        <NumberInput
                          {...register(`items.${i}.unit_value`, {
                            valueAsNumber: true,
                            min: 0,
                          })}
                          data-invalid={!!formState.errors.items?.[i]?.unit_value}
                          step={0.01}
                        />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                      <FormItem.Label>{t('common.hsTariffNumber')}</FormItem.Label>
                      <FormItem.Control>
                        <TextInput {...register(`items.${i}.hs_tariff_number`)} />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                      <FormItem.Label>{t('common.category')}</FormItem.Label>
                      <FormItem.Control>
                        <TextInput {...register(`items.${i}.category`)} />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                      <FormItem.Label>{t('common.netWeight')}</FormItem.Label>
                      <FormItem.Control invalid={!!formState.errors.items?.[i]?.unit_net_weight}>
                        <NumberInput
                          {...register(`items.${i}.unit_net_weight`, {
                            valueAsNumber: true,
                            min: 0,
                          })}
                          data-invalid={!!formState.errors.items?.[i]?.unit_net_weight}
                          decimals={false}
                          suffix="g"
                        />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                      <FormItem.Label>{t('common.grossWeight')}</FormItem.Label>
                      <FormItem.Control invalid={!!formState.errors.items?.[i]?.unit_gross_weight}>
                        <NumberInput
                          {...register(`items.${i}.unit_gross_weight`, {
                            valueAsNumber: true,
                            min: 0,
                          })}
                          data-invalid={!!formState.errors.items?.[i]?.unit_gross_weight}
                          decimals={false}
                          suffix="g"
                        />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '1 / span 6' }}>
                      <FormItem.Label>{t('common.brand')}</FormItem.Label>
                      <FormItem.Control>
                        <TextInput {...register(`items.${i}.brand`)} />
                      </FormItem.Control>
                    </FormItem.Root>

                    <FormItem.Root css={{ gridColumn: '7 / -1' }}>
                      <FormItem.Label>{t('common.countryOfOrigin')}</FormItem.Label>
                      <FormItem.Control>
                        <Controller
                          name={`items.${i}.country_of_origin`}
                          control={control}
                          render={({ field }) => (
                            <FormItem.Control>
                              <CountrySelect
                                {...field}
                                onChange={value => {
                                  field.onChange(value);
                                  field.onBlur();
                                }}
                              />
                            </FormItem.Control>
                          )}
                        />
                      </FormItem.Control>
                    </FormItem.Root>
                  </div>
                </ModalLayout.InnerContent>
              ))}

              <ModalLayout.Footer>
                <Button variant="secondary" onClick={handleAddItem}>
                  <Add16 />
                  {t('common.addItem')}
                </Button>
              </ModalLayout.Footer>
            </ModalLayout.Section>
          </ModalLayout.Body>

          <Modal.Footer>
            <Modal.CloseButton>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Modal.CloseButton>

            <FormControl.SubmitButton asChild>
              <Button>{t('common.save')}</Button>
            </FormControl.SubmitButton>
          </Modal.Footer>

          <FormLeaveBlocker formState={formState} />
        </Modal.Content>
      </Modal.Root>
    </FormControl.Root>
  );
}
